import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import JoditEditor from 'jodit-react/build/react-jodit'
import 'jodit/es5/jodit.min.css'
import '../styles/rich-text-editor.css'

const MODE_SOURCE = 2

const DEFAULT_CONFIG = {
  toolbarAdaptive: false,
  askBeforePasteHTML: false,
  defaultActionOnPaste: 'insert_as_html',
  allowResizeY: true,
  useSplitMode: false,
  buttons:
    'source,|,paragraph,bold,italic,underline,strikethrough,|,ul,ol,|,' +
    'link,image,table,|,left,center,right,|,undo,redo,|,eraser',
}

const cleanHtml = (content) =>
  (content || '').replace(/<div>/g, '').replace(/<\/div>/g, '')

const getMirrorValue = (editor) => {
  const mirror = editor.container?.querySelector('.jodit-source__mirror')
  if (mirror && typeof mirror.value === 'string') {
    return mirror.value
  }
  return editor.value
}

export const RichTextEditor = ({
  value = '',
  onChange,
  readOnly = false,
  height = '300px',
  id = '',
  config,
}) => {
  const onChangeRef = useRef(onChange)
  const lastEmittedRef = useRef(value || '')

  onChangeRef.current = onChange

  useEffect(() => {
    lastEmittedRef.current = value || ''
  }, [value])

  const emitToParent = useCallback((content) => {
    const cleanContent = cleanHtml(content)
    if (cleanContent === lastEmittedRef.current) return
    lastEmittedRef.current = cleanContent
    if (onChangeRef.current) {
      onChangeRef.current(cleanContent)
    }
  }, [])

  const emitToParentRef = useRef(emitToParent)
  emitToParentRef.current = emitToParent

  const initEditor = useCallback((editor) => {
    let pendingSourceContent = null

    editor.events
      .off('.richTextEditor')
      .on('beforeSetMode.richTextEditor', () => {
        if (editor.getMode() === MODE_SOURCE) {
          pendingSourceContent = getMirrorValue(editor)
        }
      })
      .on('afterSetMode.richTextEditor', () => {
        if (pendingSourceContent !== null) {
          const content = pendingSourceContent
          pendingSourceContent = null
          editor.value = cleanHtml(content)
          emitToParentRef.current(content)
        }
      })
      .on('change.richTextEditor', (newValue) => {
        if (editor.getMode() === MODE_SOURCE) return
        emitToParentRef.current(newValue)
      })
  }, [])

  const editorConfig = useMemo(
    () => ({
      ...DEFAULT_CONFIG,
      ...(config?.buttons ? { buttons: config.buttons } : {}),
      readonly: readOnly,
      height,
    }),
    [config?.buttons, readOnly, height]
  )

  return (
    <div className="rich-text-editor-wrapper">
      <JoditEditor
        id={id}
        value={value}
        onChange={() => {}}
        editorRef={initEditor}
        config={editorConfig}
      />
    </div>
  )
}
